import { useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";
import { useAuth, API } from "../App";
import { toast } from "sonner";
import Sidebar from "../components/Sidebar";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Switch } from "../components/ui/switch";
import { Badge } from "../components/ui/badge";
import { ScrollArea } from "../components/ui/scroll-area";
import ViewSelector from "../components/ViewSelector";
import {
  FileStack,
  Plus,
  Loader2,
  Edit,
  Trash2,
  Upload,
  FileText,
  CheckCircle,
  GripVertical,
  X,
  Wand2,
  ArrowUp,
  ArrowDown,
  Columns,
  Search,
  Package
} from "lucide-react";

const FIELD_TYPES = [
  { value: "text", label: "Texto" },
  { value: "number", label: "Número" },
  { value: "date", label: "Fecha" },
  { value: "textarea", label: "Texto largo" },
  { value: "select", label: "Lista de opciones" },
  { value: "checkbox", label: "Casilla de verificación" }
];

const COLUMN_WIDTHS = [
  { value: "full", label: "Ancho completo", icon: "▬▬▬▬" },
  { value: "half", label: "Mitad (1/2)", icon: "▬▬" },
  { value: "third", label: "Tercio (1/3)", icon: "▬" }
];

export default function PlantillasGHPage() {
  const [plantillas, setPlantillas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [showUploadDialog, setShowUploadDialog] = useState(false);
  const [editingPlantilla, setEditingPlantilla] = useState(null);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [currentView, setCurrentView] = useState("cards");
  const [formData, setFormData] = useState({
    name: "",
    description: "",
    fields: []
  });
  const [draggedIndex, setDraggedIndex] = useState(null);
  const fileInputRef = useRef(null);
  const { getAuthHeaders } = useAuth();

  // Filter plantillas
  const filteredPlantillas = plantillas.filter(p =>
    p.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    p.description?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const fetchPlantillas = useCallback(async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API}/gestion-humana/plantillas`, getAuthHeaders());
      setPlantillas(res.data);
    } catch (err) {
      toast.error("Error al cargar plantillas");
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders]);

  useEffect(() => {
    fetchPlantillas();
  }, [fetchPlantillas]);

  const resetForm = () => {
    setFormData({
      name: "",
      description: "",
      fields: []
    });
    setEditingPlantilla(null);
  };

  const openNewDialog = () => {
    resetForm();
    setShowDialog(true);
  };

  const openUploadDialog = () => {
    setShowUploadDialog(true);
  };

  const openEditDialog = (plantilla) => {
    setEditingPlantilla(plantilla);
    setFormData({
      name: plantilla.name,
      description: plantilla.description || "",
      fields: (plantilla.fields || []).map((f, idx) => ({
        ...f,
        column_width: f.column_width || "full",
        order: f.order ?? idx
      }))
    });
    setShowDialog(true);
  };

  const addField = () => {
    const newField = {
      id: `field_${Date.now()}`,
      name: "",
      label: "",
      field_type: "text",
      required: false,
      options: [],
      column_width: "full",
      order: formData.fields.length
    };
    setFormData(prev => ({
      ...prev,
      fields: [...prev.fields, newField]
    }));
  };

  const updateField = (index, key, value) => {
    setFormData(prev => ({
      ...prev,
      fields: prev.fields.map((f, i) => 
        i === index ? { ...f, [key]: value } : f
      )
    }));
  };

  const removeField = (index) => {
    setFormData(prev => ({
      ...prev,
      fields: prev.fields.filter((_, i) => i !== index)
    }));
  };

  const moveField = (index, direction) => {
    const newIndex = direction === 'up' ? index - 1 : index + 1;
    if (newIndex < 0 || newIndex >= formData.fields.length) return;
    
    const newFields = [...formData.fields];
    [newFields[index], newFields[newIndex]] = [newFields[newIndex], newFields[index]];
    // Update order
    newFields.forEach((f, idx) => f.order = idx);
    setFormData(prev => ({ ...prev, fields: newFields }));
  };

  const handleDragStart = (e, index) => {
    setDraggedIndex(index);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e, index) => {
    e.preventDefault();
    if (draggedIndex === null || draggedIndex === index) return;
    
    const newFields = [...formData.fields];
    const draggedField = newFields[draggedIndex];
    newFields.splice(draggedIndex, 1);
    newFields.splice(index, 0, draggedField);
    newFields.forEach((f, idx) => f.order = idx);
    
    setFormData(prev => ({ ...prev, fields: newFields }));
    setDraggedIndex(index);
  };

  const handleDragEnd = () => {
    setDraggedIndex(null);
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploading(true);
    const formDataUpload = new FormData();
    formDataUpload.append('file', file);

    try {
      const res = await axios.post(
        `${API}/gestion-humana/plantillas/generate-from-file`, 
        formDataUpload,
        {
          ...getAuthHeaders(),
          headers: {
            ...getAuthHeaders().headers,
            'Content-Type': 'multipart/form-data'
          }
        }
      );
      
      const fieldsWithWidth = (res.data.fields || []).map((f, idx) => ({
        ...f,
        column_width: f.column_width || "full",
        order: idx
      }));
      
      setFormData({
        name: res.data.suggested_name || "",
        description: res.data.description || "",
        fields: fieldsWithWidth
      });
      
      setShowUploadDialog(false);
      setShowDialog(true);
      toast.success(`${res.data.fields?.length || 0} campos extraídos del documento`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al procesar el archivo");
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleSave = async () => {
    if (!formData.name.trim()) {
      toast.error("El nombre es requerido");
      return;
    }
    if (formData.fields.length === 0) {
      toast.error("Debe agregar al menos un campo");
      return;
    }

    // Validate fields have labels
    const invalidField = formData.fields.find(f => !f.label.trim());
    if (invalidField) {
      toast.error("Todos los campos deben tener una etiqueta");
      return;
    }

    // Prepare fields with auto-generated names and order
    const fieldsToSave = formData.fields.map((f, idx) => ({
      id: f.id || `field_${Date.now()}_${idx}`,
      name: f.name || f.label.toLowerCase().replace(/\s+/g, '_').replace(/[^a-z0-9_]/g, ''),
      label: f.label,
      field_type: f.field_type || "text",
      required: f.required || false,
      options: f.options || [],
      column_width: f.column_width || "full",
      order: idx
    }));

    setSaving(true);
    try {
      if (editingPlantilla) {
        await axios.put(`${API}/gestion-humana/plantillas/${editingPlantilla.id}`, {
          name: formData.name,
          description: formData.description,
          fields: fieldsToSave
        }, getAuthHeaders());
        toast.success("Plantilla actualizada");
      } else {
        await axios.post(`${API}/gestion-humana/plantillas`, {
          name: formData.name,
          description: formData.description,
          fields: fieldsToSave
        }, getAuthHeaders());
        toast.success("Plantilla creada");
      }
      setShowDialog(false);
      resetForm();
      fetchPlantillas();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al guardar");
    } finally {
      setSaving(false);
    }
  };

  const deletePlantilla = async (id) => {
    if (!window.confirm("¿Estás seguro de eliminar esta plantilla?")) return;
    try {
      await axios.delete(`${API}/gestion-humana/plantillas/${id}`, getAuthHeaders());
      toast.success("Plantilla eliminada");
      fetchPlantillas();
    } catch (err) {
      toast.error("Error al eliminar");
    }
  };

  // Group fields by rows based on column_width
  const getFieldsPreview = (fields) => {
    if (!fields || fields.length === 0) return [];
    const sortedFields = [...fields].sort((a, b) => (a.order || 0) - (b.order || 0));
    return sortedFields.slice(0, 4);
  };

  return (
    <div className="flex min-h-screen bg-slate-50 dark:bg-slate-900">
      <Sidebar />
      
      <main className="flex-1 ml-0 md:ml-64 p-4 md:p-8 pt-14 md:pt-8 overflow-x-hidden" data-testid="plantillas-gh-page">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-3xl font-bold text-slate-900 dark:text-white font-['Manrope']">
              Plantillas de Gestión Humana
            </h1>
            <p className="text-slate-500 dark:text-slate-400 mt-1">
              Crea y administra plantillas de formularios para solicitudes
            </p>
          </div>
          <div className="flex gap-3">
            <Button 
              onClick={openUploadDialog}
              variant="outline"
              className="border-[#0C314C] text-[#0C314C] hover:bg-[#0C314C]/10"
            >
              <Wand2 className="w-4 h-4 mr-2" />
              Generar con IA
            </Button>
            <Button 
              onClick={openNewDialog}
              className="bg-[#0C314C] hover:bg-[#2a4d7a] text-white"
            >
              <Plus className="w-4 h-4 mr-2" />
              Nueva Plantilla
            </Button>
          </div>
        </div>

        {/* Stats KPIs */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <Card className="dark:bg-slate-800 dark:border-slate-700">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500 dark:text-slate-400">Total Plantillas</p>
                  <p className="text-2xl font-bold text-slate-900 dark:text-white">{plantillas.length}</p>
                </div>
                <Package className="w-8 h-8 text-blue-400" />
              </div>
            </CardContent>
          </Card>
          <Card className="dark:bg-slate-800 dark:border-slate-700">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500 dark:text-slate-400">Total Campos</p>
                  <p className="text-2xl font-bold text-green-600">{plantillas.reduce((acc, p) => acc + (p.fields?.length || 0), 0)}</p>
                </div>
                <CheckCircle className="w-8 h-8 text-green-400" />
              </div>
            </CardContent>
          </Card>
          <Card className="dark:bg-slate-800 dark:border-slate-700">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500 dark:text-slate-400">Promedio Campos</p>
                  <p className="text-2xl font-bold text-amber-600">
                    {plantillas.length > 0 ? Math.round(plantillas.reduce((acc, p) => acc + (p.fields?.length || 0), 0) / plantillas.length) : 0}
                  </p>
                </div>
                <FileText className="w-8 h-8 text-amber-400" />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Search and View Selector */}
        <div className="flex flex-wrap gap-4 mb-6 items-center justify-between">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input
              placeholder="Buscar plantillas..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10"
              data-testid="search-plantillas"
            />
          </div>
          <ViewSelector currentView={currentView} onViewChange={setCurrentView} />
        </div>

        {/* Plantillas View */}
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
          </div>
        ) : filteredPlantillas.length === 0 ? (
          <Card className="dark:bg-slate-800 dark:border-slate-700">
            <CardContent className="py-12 text-center">
              <FileStack className="w-12 h-12 text-slate-300 dark:text-slate-600 mx-auto mb-4" />
              <p className="text-slate-500 dark:text-slate-400 mb-4">
                {searchQuery ? "No se encontraron plantillas" : "No hay plantillas creadas"}
              </p>
              {!searchQuery && (
                <Button onClick={openUploadDialog} variant="outline">
                  <Wand2 className="w-4 h-4 mr-2" />
                  Crear desde documento con IA
                </Button>
              )}
            </CardContent>
          </Card>
        ) : currentView === "table" ? (
          /* Table View */
          <Card className="dark:bg-slate-800 dark:border-slate-700">
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-slate-50 dark:bg-slate-700/50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 dark:text-slate-400 uppercase">Plantilla</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 dark:text-slate-400 uppercase">Descripción</th>
                      <th className="px-4 py-3 text-center text-xs font-medium text-slate-500 dark:text-slate-400 uppercase">Campos</th>
                      <th className="px-4 py-3 text-center text-xs font-medium text-slate-500 dark:text-slate-400 uppercase">Acciones</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-200 dark:divide-slate-700">
                    {filteredPlantillas.map((plantilla) => (
                      <tr key={plantilla.id} className="hover:bg-slate-50 dark:hover:bg-slate-700/50" data-testid={`plantilla-row-${plantilla.id}`}>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-lg bg-[#0C314C] flex items-center justify-center">
                              <FileText className="w-5 h-5 text-[#45E69A]" />
                            </div>
                            <span className="font-medium text-slate-900 dark:text-white">{plantilla.name}</span>
                          </div>
                        </td>
                        <td className="px-4 py-3 text-sm text-slate-600 dark:text-slate-400 max-w-xs truncate">{plantilla.description || '-'}</td>
                        <td className="px-4 py-3 text-center">
                          <Badge variant="secondary">{plantilla.fields?.length || 0}</Badge>
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center justify-center gap-2">
                            <Button variant="ghost" size="sm" onClick={() => openEditDialog(plantilla)}>
                              <Edit className="w-4 h-4 text-slate-400" />
                            </Button>
                            <Button variant="ghost" size="sm" onClick={() => deletePlantilla(plantilla.id)}>
                              <Trash2 className="w-4 h-4 text-red-400" />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        ) : currentView === "list" ? (
          /* List View */
          <Card className="dark:bg-slate-800 dark:border-slate-700">
            <CardContent className="p-2">
              <div className="divide-y divide-slate-200 dark:divide-slate-700">
                {filteredPlantillas.map((plantilla) => (
                  <div key={plantilla.id} className="flex items-center justify-between py-3 px-4 hover:bg-slate-50 dark:hover:bg-slate-700/50 rounded" data-testid={`plantilla-list-${plantilla.id}`}>
                    <div className="flex items-center gap-4">
                      <div className="w-10 h-10 rounded-lg bg-[#0C314C] flex items-center justify-center">
                        <FileText className="w-5 h-5 text-[#45E69A]" />
                      </div>
                      <div>
                        <p className="font-medium text-slate-900 dark:text-white">{plantilla.name}</p>
                        <p className="text-sm text-slate-500 dark:text-slate-400">{plantilla.description || 'Sin descripción'}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <Badge variant="secondary">{plantilla.fields?.length || 0} campos</Badge>
                      <Button variant="ghost" size="sm" onClick={() => openEditDialog(plantilla)}>
                        <Edit className="w-4 h-4 text-slate-400" />
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => deletePlantilla(plantilla.id)}>
                        <Trash2 className="w-4 h-4 text-red-400" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        ) : (
          /* Cards View (default) */
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredPlantillas.map((plantilla) => (
              <Card 
                key={plantilla.id} 
                className="dark:bg-slate-800 dark:border-slate-700 hover:shadow-lg transition-shadow"
              >
                <CardHeader className="pb-3">
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-lg bg-[#0C314C] flex items-center justify-center">
                        <FileText className="w-5 h-5 text-[#45E69A]" />
                      </div>
                      <div>
                        <CardTitle className="text-base font-semibold text-slate-900 dark:text-white">
                          {plantilla.name}
                        </CardTitle>
                        <p className="text-xs text-slate-500 dark:text-slate-400">
                          {plantilla.fields?.length || 0} campos
                        </p>
                      </div>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  {plantilla.description && (
                    <p className="text-sm text-slate-600 dark:text-slate-400 mb-4 line-clamp-2">
                      {plantilla.description}
                    </p>
                  )}
                  
                  {/* Fields Preview */}
                  <div className="space-y-1 mb-4">
                    {getFieldsPreview(plantilla.fields).map((field, idx) => (
                      <div key={idx} className="flex items-center gap-2 text-xs">
                        <CheckCircle className="w-3 h-3 text-green-500" />
                        <span className="text-slate-600 dark:text-slate-400 truncate flex-1">
                          {field.label}
                        </span>
                        {field.column_width !== 'full' && (
                          <Badge variant="outline" className="text-[10px] px-1 py-0">
                            {field.column_width === 'half' ? '1/2' : '1/3'}
                          </Badge>
                        )}
                        {field.required && (
                          <Badge variant="outline" className="text-[10px] px-1 py-0 text-red-500">
                            *
                          </Badge>
                        )}
                      </div>
                    ))}
                    {(plantilla.fields?.length || 0) > 4 && (
                      <p className="text-xs text-slate-400 pl-5">
                        +{plantilla.fields.length - 4} más...
                      </p>
                    )}
                  </div>
                  
                  {/* Actions */}
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => openEditDialog(plantilla)}
                      className="flex-1"
                    >
                      <Edit className="w-4 h-4 mr-1" />
                      Editar
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => deletePlantilla(plantilla.id)}
                      className="text-slate-400 hover:text-red-600"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Upload Dialog */}
        <Dialog open={showUploadDialog} onOpenChange={setShowUploadDialog}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Wand2 className="w-5 h-5 text-[#0C314C]" />
                Generar Plantilla con IA
              </DialogTitle>
              <DialogDescription>
                Sube un documento y la IA extraerá los campos del formulario automáticamente
              </DialogDescription>
            </DialogHeader>
            
            <div className="py-8">
              <input
                ref={fileInputRef}
                type="file"
                accept=".pdf,.docx,.xlsx,.xls,.txt,.csv"
                onChange={handleFileUpload}
                className="hidden"
              />
              
              <div className="flex flex-col items-center">
                {uploading ? (
                  <>
                    <Loader2 className="w-12 h-12 animate-spin text-[#0C314C] mb-4" />
                    <p className="text-slate-600 dark:text-slate-400">Analizando documento con IA...</p>
                  </>
                ) : (
                  <>
                    <div className="w-20 h-20 rounded-full bg-[#0C314C]/10 flex items-center justify-center mb-4">
                      <Upload className="w-10 h-10 text-[#0C314C]" />
                    </div>
                    <Button 
                      onClick={() => fileInputRef.current?.click()}
                      className="bg-[#0C314C] hover:bg-[#2a4d7a] text-white mb-3"
                    >
                      <Upload className="w-4 h-4 mr-2" />
                      Seleccionar Documento
                    </Button>
                    <p className="text-xs text-slate-500 dark:text-slate-400 text-center">
                      Formatos soportados: PDF, Word, Excel, CSV, TXT
                    </p>
                  </>
                )}
              </div>
            </div>
            
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowUploadDialog(false)} disabled={uploading}>
                Cancelar
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Create/Edit Dialog */}
        <Dialog open={showDialog} onOpenChange={setShowDialog}>
          <DialogContent className="max-w-6xl h-[85vh] flex flex-col">
            <DialogHeader>
              <DialogTitle className="text-xl font-bold font-['Manrope']">
                {editingPlantilla ? "Editar Plantilla" : "Nueva Plantilla"}
              </DialogTitle>
              <DialogDescription>
                Define los campos del formulario. Arrastra para reordenar.
              </DialogDescription>
            </DialogHeader>
            
            <div className="flex-1 flex flex-col">
              {/* Basic Info */}
              <div className="grid grid-cols-2 gap-4 mb-4">
                <div className="space-y-2">
                  <Label htmlFor="name">Nombre de la plantilla *</Label>
                  <Input
                    id="name"
                    value={formData.name}
                    onChange={(e) => setFormData({...formData, name: e.target.value})}
                    placeholder="Ej: Solicitud de Vacaciones"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="description">Descripción</Label>
                  <Input
                    id="description"
                    value={formData.description}
                    onChange={(e) => setFormData({...formData, description: e.target.value})}
                    placeholder="Breve descripción del propósito"
                  />
                </div>
              </div>
              
              {/* Fields Section */}
              <div className="flex items-center justify-between mb-2">
                <Label className="flex items-center gap-2">
                  Campos del formulario ({formData.fields.length})
                  <span className="text-xs text-slate-400 font-normal">• Arrastra para reordenar</span>
                </Label>
                <Button variant="outline" size="sm" onClick={addField}>
                  <Plus className="w-4 h-4 mr-1" />
                  Agregar Campo
                </Button>
              </div>
              
              <ScrollArea className="flex-1 min-h-[400px] border rounded-lg p-3 dark:border-slate-700">
                {formData.fields.length === 0 ? (
                  <div className="text-center py-8 text-slate-500 dark:text-slate-400">
                    <FileText className="w-8 h-8 mx-auto mb-2 opacity-50" />
                    <p>No hay campos definidos</p>
                    <Button variant="link" onClick={addField}>Agregar primer campo</Button>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {formData.fields.map((field, index) => (
                      <div 
                        key={field.id || index}
                        draggable
                        onDragStart={(e) => handleDragStart(e, index)}
                        onDragOver={(e) => handleDragOver(e, index)}
                        onDragEnd={handleDragEnd}
                        className={`flex items-start gap-3 p-3 bg-slate-50 dark:bg-slate-800 rounded-lg border-2 transition-all ${
                          draggedIndex === index ? 'border-[#0C314C] opacity-50' : 'border-transparent'
                        } hover:border-slate-200 dark:hover:border-slate-600 cursor-move`}
                      >
                        {/* Drag Handle & Move Buttons */}
                        <div className="flex flex-col items-center gap-1 pt-1">
                          <GripVertical className="w-4 h-4 text-slate-400" />
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => moveField(index, 'up')}
                            disabled={index === 0}
                            className="h-5 w-5 p-0"
                          >
                            <ArrowUp className="w-3 h-3" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => moveField(index, 'down')}
                            disabled={index === formData.fields.length - 1}
                            className="h-5 w-5 p-0"
                          >
                            <ArrowDown className="w-3 h-3" />
                          </Button>
                        </div>
                        
                        <div className="flex-1 grid grid-cols-12 gap-3">
                          {/* Label */}
                          <div className="col-span-4">
                            <Label className="text-xs mb-1 block">Etiqueta *</Label>
                            <Input
                              value={field.label}
                              onChange={(e) => updateField(index, 'label', e.target.value)}
                              placeholder="Nombre del campo"
                              className="h-8 text-sm"
                            />
                          </div>
                          
                          {/* Type */}
                          <div className="col-span-2">
                            <Label className="text-xs mb-1 block">Tipo</Label>
                            <Select 
                              value={field.field_type} 
                              onValueChange={(value) => updateField(index, 'field_type', value)}
                            >
                              <SelectTrigger className="h-8 text-sm">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                {FIELD_TYPES.map(t => (
                                  <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                          
                          {/* Column Width */}
                          <div className="col-span-2">
                            <Label className="text-xs mb-1 block flex items-center gap-1">
                              <Columns className="w-3 h-3" />
                              Ancho
                            </Label>
                            <Select 
                              value={field.column_width || 'full'} 
                              onValueChange={(value) => updateField(index, 'column_width', value)}
                            >
                              <SelectTrigger className="h-8 text-sm">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                {COLUMN_WIDTHS.map(w => (
                                  <SelectItem key={w.value} value={w.value}>
                                    <span className="flex items-center gap-2">
                                      <span className="text-xs font-mono text-slate-400">{w.icon}</span>
                                      {w.label}
                                    </span>
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                          
                          {/* Required & Delete */}
                          <div className="col-span-4 flex items-end gap-2 justify-end">
                            <div className="flex items-center gap-1 px-2 py-1 bg-white dark:bg-slate-700 rounded border">
                              <Switch
                                checked={field.required}
                                onCheckedChange={(checked) => updateField(index, 'required', checked)}
                                className="scale-75"
                              />
                              <span className="text-xs text-slate-500">Requerido</span>
                            </div>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => removeField(index)}
                              className="h-8 w-8 p-0 text-slate-400 hover:text-red-600"
                            >
                              <X className="w-4 h-4" />
                            </Button>
                          </div>
                          
                          {/* Options for select type */}
                          {field.field_type === 'select' && (
                            <div className="col-span-12">
                              <Label className="text-xs mb-1 block">Opciones (separadas por coma)</Label>
                              <Input
                                value={field.options?.join(', ') || ''}
                                onChange={(e) => updateField(index, 'options', e.target.value.split(',').map(o => o.trim()).filter(Boolean))}
                                placeholder="Opción 1, Opción 2, Opción 3"
                                className="h-8 text-sm"
                              />
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </ScrollArea>
              
              {/* Layout Preview */}
              {formData.fields.length > 0 && (
                <div className="mt-4 p-3 bg-slate-100 dark:bg-slate-800 rounded-lg">
                  <Label className="text-xs text-slate-500 mb-2 block">Vista previa del layout:</Label>
                  <div className="flex flex-wrap gap-2">
                    {formData.fields.map((field, idx) => (
                      <div 
                        key={idx}
                        className={`h-6 rounded bg-[#0C314C]/20 border border-[#0C314C]/30 flex items-center justify-center text-xs text-[#0C314C] dark:text-[#45E69A] ${
                          field.column_width === 'full' ? 'w-full' : 
                          field.column_width === 'half' ? 'w-[calc(50%-4px)]' : 
                          'w-[calc(33.33%-6px)]'
                        }`}
                      >
                        {field.label || `Campo ${idx + 1}`}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
            
            <DialogFooter className="border-t dark:border-slate-700 pt-4">
              <Button variant="outline" onClick={() => setShowDialog(false)}>
                Cancelar
              </Button>
              <Button 
                onClick={handleSave} 
                disabled={saving}
                className="bg-[#0C314C] hover:bg-[#2a4d7a] text-white"
              >
                {saving && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
                {saving ? "Guardando..." : "Guardar Plantilla"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </main>
    </div>
  );
}
